import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@mui/material';
import { jxScrollbarThin } from '../../../JxControls/jxScrollbarStyle';
import { tooltipSx } from '../jernyTooltipStyling';
import './JInspectorSelect.css';

// Rough dropdown height budget — used only to decide open direction, not as exact size.
const ESTIMATED_DROPDOWN_HEIGHT = 220;

// Option tooltip needs to render above the dropdown itself (zIndex 10001) — merged into one
// componentsProps object rather than also passing a separate PopperProps, since passing both
// componentsProps.popper and PopperProps at once is ambiguous about which wins and was
// swallowing the zIndex override, leaving the tooltip stuck behind the dropdown panel.
const optionTooltipProps = {
    tooltip: tooltipSx.tooltip,
    popper: { style: { ...tooltipSx.popper.style, zIndex: 10002 } },
};

/**
 * Single option row — only shows its tooltip when the label is actually truncated
 * (scrollWidth > clientWidth). Split out from the map() in JInspectorSelect because
 * that measurement needs its own ref + effect per row.
 */
function JInspectorOption({ label, className, onMouseEnter, onMouseDown }) {
    const [truncated, setTruncated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (el) setTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    return (
        <Tooltip title={label} placement="bottom" componentsProps={optionTooltipProps} disableHoverListener={!truncated}>
            <div ref={ref} className={className} onMouseEnter={onMouseEnter} onMouseDown={onMouseDown}>
                {label}
            </div>
        </Tooltip>
    );
}

/**
 * JInspectorSelect — typeahead dropdown styled to match the native j-inspector-select look
 * (unlike JxSelect, which has its own JxControls theme). Same interaction pattern as JxSelect
 * (portal, typeahead filter, keyboard nav, flip-upward near viewport bottom) — copy exists so
 * JInspector rows can keep their own visual language without depending on JxControls styling.
 * @param {string} [id]
 * @param {number|string} value
 * @param {(value: number|string) => void} onChange
 * @param {Array<{label:string,value:number|string}>} options
 * @param {string} [defaultLabel]
 */
export default function JInspectorSelect({ id, value, onChange, options, defaultLabel = '—' }) {
    const selected = options.find((o) => o.value === value) ?? null;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(0);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const inputRef = useRef(null);
    const rootRef = useRef(null);

    const filtered = query.trim() ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options;

    // Reset highlight when filtered list changes
    useEffect(() => {
        setHighlighted(0);
    }, [query]);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handle = (e) => {
            if (!rootRef.current?.contains(e.target)) close();
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [open]);

    function openDropdown() {
        const rect = rootRef.current?.getBoundingClientRect();
        if (rect) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            const openUpward = spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

            setDropdownStyle(
                openUpward
                    ? {
                          position: 'fixed',
                          bottom: window.innerHeight - rect.top + 4,
                          left: rect.left,
                          width: rect.width,
                          maxHeight: spaceAbove - 8,
                          zIndex: 10001,
                      }
                    : {
                          position: 'fixed',
                          top: rect.bottom + 4,
                          left: rect.left,
                          width: rect.width,
                          maxHeight: spaceBelow - 8,
                          zIndex: 10001,
                      },
            );
        }

        setQuery('');
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
    }

    function close() {
        setOpen(false);
        setQuery('');
    }

    function select(option) {
        onChange(option.value);
        close();
    }

    function handleKeyDown(e) {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
            if (filtered[highlighted]) {
                e.preventDefault();
                select(filtered[highlighted]);
            }
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // prevent JxForm global Escape-to-close from firing
            close();
        }
    }

    const dropdown = open &&
        createPortal(
            <div className="j-inspector-dropdown" style={dropdownStyle}>
                <input
                    ref={inputRef}
                    className="j-inspector-select-search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search…"
                />
                {/* jxScrollbarThin goes on THIS div — it's the actual overflow-y:auto scroll
                    container, not the outer dropdown wrapper (which has overflow:hidden). */}
                <div className={`j-inspector-listbox ${jxScrollbarThin}`}>
                    {filtered.length === 0 && <div className="j-inspector-no-options">No options</div>}
                    {filtered.map((o, i) => (
                        <JInspectorOption
                            key={o.value}
                            label={o.label}
                            className={[
                                'j-inspector-option',
                                o.value === value ? 'j-inspector-option--selected' : '',
                                i === highlighted ? 'j-inspector-option--active' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onMouseEnter={() => setHighlighted(i)}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                select(o);
                            }}
                        />
                    ))}
                </div>
            </div>,
            document.body,
        );

    return (
        <div ref={rootRef} id={id} className={`j-inspector-select j-inspector-select--trigger${open ? ' j-inspector-select--open' : ''}`} onClick={openDropdown}>
            <span className="j-inspector-select-label">{selected?.label ?? defaultLabel}</span>
            <span className="j-inspector-select-arrow">{open ? '▲' : '▼'}</span>
            {dropdown}
        </div>
    );
}
